<template>
  <SfButton
    :type="isSubmitType ? 'submit' : 'button'"
    :link="goToRoute"
    class="button-base rounded text-center normal-case"
    :class="[
      classList,
      {'w-max rounded-full lg:w-full lg:rounded-none': isRoundChevronMobile},
      { 'font-semibold': !thinText },
    ]"
    :disabled="disabled"
    @click="$emit('click')"
  >
    <slot name="icon-left" />
    <slot
      name="default"
      :default-content="formattedText"
    >
      <span
        v-if="formattedText"
        :class="[{'hidden lg:block': isRoundChevronMobile}]"
      >{{ formattedText }}</span>
      <i
        v-if="isRoundChevronMobile"
        class="iconify svg--chevron-right block lg:hidden"
      />
    </slot>
    <slot name="icon-right" />
  </SfButton>
</template>

<script lang="ts">
import {
  ButtonSizesArray,
  ButtonTypesArray,
} from '~/components/atoms/Buttons/Button/ButtonTypes';
import type {
  ButtonSizes,
  ButtonType,
} from '~/components/atoms/Buttons/Button/ButtonTypes';
import SfButton from '~/components/atoms/SfButton/SfButton.vue';

export default defineComponent({
  name: 'CustomButton',
  components: {
    SfButton,
  },
  props: {
    /**
     * The TYPE of button
     * @values primary, secondary, tertiary...
     * */
    type: {
      type: String as PropType<ButtonType>,
      default: 'tertiary',
      validator: (val: ButtonType) => ButtonTypesArray.includes(val),
    },
    /** Is this a form submit button? */
    isSubmitType: {
      type: Boolean,
      default: false,
    },
    /** Translated text for the button */
    text: {
      type: String,
      default: '',
    },
    /**
     * Desired button size
     * @values sm, md, lg
     */
    size: {
      type: String as PropType<ButtonSizes>,
      default: 'lg',
      validator: (val: ButtonSizes) => ButtonSizesArray.includes(val),
    },
    /** URL to follow on click */
    url: {
      type: String,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isRoundChevronMobile: {
      type: Boolean,
      default: false,
    },
    fullWidth: {
      type: Boolean,
      default: true,
    },
    thinText: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['click'],
  setup(props) {

    const formattedText = computed(() => String(props.text));
    const { addPrefixToSlugIfNecessary } = usePrefix();
    const goToRoute = computed(() => addPrefixToSlugIfNecessary(props.url));
    const loadingClass = computed(() => props.loading && 'animate-pulse button--loading');

    const classList = computed(() => {

      const classes = [];

      if (props.type !== 'raw' && props.fullWidth) {

        classes.push('w-full');
      
      }

      if (props.type !== 'raw') {

        classes.push(props.size);
      
      }

      classes.push(loadingClass.value, props.type);

      return classes;
    
    });

    return {
      loadingClass,
      formattedText,
      goToRoute,
      classList,
    };
  
  },
});

</script>

<style lang="scss" scoped>
.sm {
  @apply p-1;
}

.md {
  @apply p-2;
}

.lg {
  @apply p-3;
}

.button-base {
  @apply appearance-none whitespace-normal;

  &:link,
  &:visited {
    @apply no-underline;
  }
}

.primary {
  @apply bg-EXPONDO-primary text-EXPONDO-white;

  &:hover,
  &:active {
    @apply bg-EXPONDO-primary-80;
  }

  &.is-disabled--button,
  &:disabled {
    @apply bg-EXPONDO-grey-60 text-EXPONDO-white;
  }
}

.secondary {
  @apply border border-EXPONDO-primary bg-EXPONDO-white text-EXPONDO-primary;

  &:hover,
  &:active {
    @apply border-EXPONDO-primary-80 text-EXPONDO-primary-80;
  }

  &.is-disabled--button,
  &:disabled {
    @apply border-EXPONDO-grey-20 text-EXPONDO-grey-60;
  }
}

.tertiary {
  @apply bg-EXPONDO-gold text-EXPONDO-primary;

  &:hover,
  &:active {
    @apply bg-EXPONDO-gold-dark;
  }

  &.is-disabled--button,
  &:disabled {
    @apply bg-EXPONDO-grey-60 text-EXPONDO-white;
  }
}

.quaternary {
  @apply bg-EXPONDO-secondary text-EXPONDO-white;

  &:hover,
  &:active {
    @apply bg-EXPONDO-primary-80;
  }

  &.is-disabled--button,
  &:disabled {
    @apply bg-EXPONDO-grey-60 text-EXPONDO-white;
  }
}

.quinary {
  @apply bg-EXPONDO-red text-EXPONDO-white;

  &:hover,
  &:active {
    @apply bg-EXPONDO-red-dark;
  }

  &.is-disabled--button,
  &:disabled {
    @apply bg-EXPONDO-grey-60 text-EXPONDO-white;
  }
}

.senary {
  @apply bg-EXPONDO-secondary-medium text-EXPONDO-black;

  &:hover,
  &:active {
    @apply bg-EXPONDO-secondary;
  }

  &.is-disabled--button,
  &:disabled {
    @apply bg-EXPONDO-grey-60 text-EXPONDO-white;
  }
}

.link {
  @apply normal-case text-EXPONDO-secondary underline;

  &:hover,
  &:active {
    @apply bg-transparent text-EXPONDO-primary-80;
  }

  &.is-disabled--button,
  &:disabled {
    @apply text-EXPONDO-grey-60;
  }
}

.link-dark {
  @apply normal-case text-EXPONDO-primary underline;

  &:hover,
  &:active {
    @apply bg-transparent text-EXPONDO-primary-80;
  }

  &.is-disabled--button,
  &:disabled {
    @apply text-EXPONDO-grey-80;
  }
}

.highlight {
  @apply p-4 border rounded-md text-EXPONDO-primary border-EXPONDO-primary;
  @apply bg-EXPONDO-secondary-light h-full w-auto font-normal whitespace-nowrap;
}

.raw {
  padding: 0;

  &.is-disabled--button,
  &:disabled {
    background: unset;
  }
}

:deep(.sf-loader__overlay) {
  background: unset;
}
</style>
